import { type PropsWithChildren, useMemo } from 'react';

import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import { getAuthUser } from '@/shared/auth/selectors';
import Button from '@/shared/ui/Button';
import WarningBanner from '@/shared/ui/WarningBanner';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { openSendMessageDialog } from '@/teamtxt/dialogs';
import { getConversationsLoaded } from '@/workchat/v2/store/conversations/conversationsSelectors';
import { toggleShow } from '@/workchat/v2/store/reducer';
import { getWorkchatConnected, getWorkchatInitError } from '@/workchat/v2/store/selectors';

export type HeaderButton = {
  action: () => void;
  className?: string;
  disabled?: boolean;
  label: (string | JSX.Element)[] | string | JSX.Element;
  title?: string;
  side: 'left' | 'right';
  color?: 'secondary' | 'primary';
  circle?: boolean;
};

interface HeaderProps {
  showBroadcast?: boolean;
  buttons?: HeaderButton[];
}

export default function HeaderV2({ showBroadcast, buttons, children }: PropsWithChildren<HeaderProps>) {
  const dispatch = useWiwDispatch();
  const authedUser = useWiwSelector(getAuthUser);
  const connected = useWiwSelector(getWorkchatConnected);
  const initError = useWiwSelector(getWorkchatInitError);
  const conversationsLoaded = useWiwSelector(getConversationsLoaded);
  const canBroadcast = authedUser.canSupervise();
  const teamTextEnabled = useLDFlag('FDT-1353-team-text');

  const onTeamTxtClick = () => {
    dispatch(toggleShow(false));
    dispatch(openSendMessageDialog());
  };

  const leftButtons = useMemo(() => {
    return buttons?.filter(button => button.side === 'left');
  }, [buttons]);

  const rightButtons = useMemo(() => {
    return buttons?.filter(button => button.side === 'right');
  }, [buttons]);

  const createButton = useMemo(() => {
    return (button: HeaderButton, index: number) => (
      <Button
        type="button"
        key={`head-button-${button.title}-${index}`}
        title={button.title}
        onClick={button.action}
        className={button.className}
        disabled={button.disabled || false}
        size="sm"
        color={button.color}
        circle={button.circle}
      >
        {button.label}
      </Button>
    );
  }, [buttons, connected]);

  if (!(canBroadcast && showBroadcast) && !buttons?.length && !children) {
    return null;
  }

  const renderDisconnectedBanner = () => {
    if (connected || initError || !conversationsLoaded) {
      return null;
    }

    return (
      <div className="col-12">
        <WarningBanner icon="warning" level="error" wrapperClass="disconnected-banner">
          Service unavailable, trying to reconnect...
        </WarningBanner>
      </div>
    );
  };

  return (
    <div className="row workchat-header">
      <div className="col-12">
        <div className="row justify-content-between align-items-center header">
          <div className="col-3">
            <div className="row no-gutters float-left align-items-center">
              {canBroadcast && showBroadcast && (
                <Button type="button" size="sm" onClick={onTeamTxtClick} color="secondary" className="btn-left-action">
                  {teamTextEnabled ? 'Send Email/TeamTXT' : 'Send Email'}
                </Button>
              )}
              {leftButtons?.map((button, idx) => createButton(button, idx))}
            </div>
          </div>
          {children ? <h2 className="col-6 header">{children}</h2> : <div className="filler" />}
          <div className="col-3">
            <div className="row no-gutters float-right align-items-center">
              {rightButtons?.map((button, idx) => createButton(button, idx))}
            </div>
          </div>
        </div>
      </div>
      {renderDisconnectedBanner()}
    </div>
  );
}
